import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  FileJson,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { apiCall } from "../api";
import { useMountEffect } from "../hooks/useMountEffect";
import "./WorkOrdersList.css";

const formatWorkOrderRuntime = (durationSeconds) => {
  if (!durationSeconds || durationSeconds <= 0)
    return { display: "-", color: "#a0aec0", bg: "transparent" };

  const seconds = durationSeconds % 60;
  const minutes = Math.floor(durationSeconds / 60) % 60;
  const hours = Math.floor(durationSeconds / 3600);
  const totalMinutes = durationSeconds / 60;

  let display = "";
  if (hours > 0) {
    display = `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    display = `${minutes}m ${seconds}s`;
  } else {
    display = `${seconds}s`;
  }

  // Color coding based on total minutes
  let color = "#a0aec0";
  let bg = "transparent";

  if (totalMinutes >= 5) {
    color = "#991B1B";
    bg = "#FEE2E2";
  } else if (totalMinutes >= 2.5) {
    color = "#B45309";
    bg = "#FEF3C7";
  } else if (totalMinutes > 0) {
    color = "#166534";
    bg = "#DCFCE7";
  }

  return { display, color, bg };
};

// Custom Dropdown Component
const CustomStatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  const options = [
    { value: "all", label: "✓ All Status" },
    { value: "created", label: "○ Created" },
    { value: "processing", label: "⟳ Processing" },
    { value: "completed", label: "✔ Completed" },
    { value: "failed", label: "✕ Failed" },
  ];

  const selectedOption = options.find((opt) => opt.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="custom-dropdown-wrapper" ref={dropdownRef}>
      <button
        className="custom-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{selectedOption?.label}</span>
        <svg
          className={`dropdown-arrow ${isOpen ? "open" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      {isOpen && (
        <div className="custom-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              className={`custom-dropdown-option ${value === option.value ? "selected" : ""}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <span className="option-icon">{option.label.split(" ")[0]}</span>
              <span className="option-text">
                {option.label.split(" ").slice(1).join(" ")}
              </span>
              {value === option.value && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const WorkOrdersList = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1,
    limit: 10,
    skip: 0,
  });
  const [stats, setStats] = useState({
    total: 0,
    processed: 0,
    processing: 0,
    pending: 0,
    failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useMountEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    // Check if there's a pending refresh signal from notifications
    const refreshSignal = localStorage.getItem("workOrdersRefreshSignal");
    if (refreshSignal) {
      console.log(
        "[WorkOrdersList] 📡 Found refresh signal on mount, refetching...",
      );
      localStorage.removeItem("workOrdersRefreshSignal");
      fetchWorkOrders(0);
    } else {
      fetchWorkOrders(0);
    }
  });

  // Listen for work order refresh event from MainLayout (triggered by extraction notifications)
  useEffect(() => {
    const handleRefreshWorkOrders = (event) => {
      const { workOrderId, type, title } = event.detail;
      console.log("[WorkOrdersList] 🔄 REFRESH TRIGGERED:", {
        workOrderId,
        type,
        title,
      });

      console.log("[WorkOrdersList] 📡 Refetching work orders from API...");
      // Refetch work orders to get latest status
      // Use current pagination state via closure
      fetchWorkOrders(0); // Reset to first page to see updated status
    };

    // Listen for refresh event from MainLayout
    window.addEventListener("refreshWorkOrders", handleRefreshWorkOrders);

    // Cleanup
    return () => {
      console.log("[WorkOrdersList] Removing refresh listener");
      window.removeEventListener("refreshWorkOrders", handleRefreshWorkOrders);
    };
  }, []); // Empty dependency - listener should persist throughout component lifetime

  const fetchWorkOrders = async (skip = 0) => {
    try {
      setLoading(true);
      const limit = 10;
      const responseData = await apiCall(
        `/api/work-orders?skip=${skip}&limit=${limit}`,
      );

      // Handle new pagination response format
      if (responseData.data && responseData.pagination) {
        setWorkOrders(responseData.data);
        setPagination(responseData.pagination);

        // Calculate stats from returned data only
        const data = responseData.data;
        const stats = {
          total: responseData.pagination.total,
          processed: data.filter((wo) => wo.status === "completed").length,
          processing: data.filter((wo) => wo.status === "processing").length,
          pending: data.filter((wo) => wo.status === "created").length,
          failed: data.filter((wo) => wo.status === "failed").length,
        };
        setStats(stats);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching work orders:", error);
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      const newSkip = pagination.skip + pagination.limit;
      fetchWorkOrders(newSkip);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      const newSkip = Math.max(0, pagination.skip - pagination.limit);
      fetchWorkOrders(newSkip);
    }
  };

  const filteredWorkOrders = workOrders.filter((wo) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      wo.traceId?.toLowerCase().includes(term) ||
      wo.customerName?.toLowerCase().includes(term) ||
      wo._id?.toLowerCase().includes(term);
    const matchesStatus = statusFilter === "all" || wo.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDownloadReport = async (workOrderId, reportType) => {
    try {
      const data = await apiCall(
        `/api/work-orders/${workOrderId}/reports/generate`,
        {
          method: "POST",
          body: JSON.stringify({ reportType }),
        },
      );

      if (!data || !data.reportId) throw new Error("Failed to generate report");

      // Fetch report file as blob
      const response = await fetch(
        `/api/work-orders/${workOrderId}/reports/${data.reportId}/download`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) throw new Error("Failed to download report");

      const blob = await response.blob();
      const element = document.createElement("a");
      element.setAttribute("href", URL.createObjectURL(blob));
      element.setAttribute(
        "download",
        `report-${workOrderId}.${reportType.toLowerCase()}`,
      );
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      console.error("Error downloading report:", error);
      alert(`Failed to download ${reportType} report`);
    }
  };

  return (
    <div className="work-orders-list">
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card processed">
          <div className="stat-value">{stats.processed}</div>
          <div className="stat-label">Processed</div>
        </div>
        <div className="stat-card processing">
          <div className="stat-value">{stats.processing}</div>
          <div className="stat-label">Processing</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card failed">
          <div className="stat-value">{stats.failed}</div>
          <div className="stat-label">Failed</div>
        </div>
      </div>

      {/* Header Section */}
      <div className="work-orders-header">
        <div className="header-left">
          <input
            type="text"
            placeholder="Search by Trace ID, Name, or Work Order ID"
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="header-center">
          <CustomStatusDropdown
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>
        <div className="header-right">
          <button
            className="btn-create-order"
            onClick={() => navigate("/create-work-order")}
          >
            <span>+</span> New Work Order
          </button>
        </div>
      </div>

      {/* Work Orders Table */}
      <div className="table-container">
        <table className="work-orders-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>File Progress</th>
              <th>Trace ID</th>
              <th>Mode</th>
              <th>Status</th>
              <th>⏱️ Runtime</th>
              <th>Form Type</th>
              <th>Created at</th>
              <th>Created by</th>
              <th>Reports</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="10"
                  style={{ textAlign: "center", padding: "2rem" }}
                >
                  Loading work orders...
                </td>
              </tr>
            ) : filteredWorkOrders.length === 0 ? (
              <tr>
                <td
                  colSpan="10"
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#9ca3af",
                  }}
                >
                  No work orders found
                </td>
              </tr>
            ) : (
              filteredWorkOrders.map((wo) => (
                <tr key={wo._id}>
                  <td className="mono small">
                    <button
                      onClick={() => navigate(`/work-order/${wo._id}`)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#3b82f6",
                        cursor: "pointer",
                        textDecoration: "underline",
                        padding: 0,
                        font: "inherit",
                      }}
                    >
                      {wo._id?.toString().slice(-8)}
                    </button>
                  </td>
                  <td>
                    <span style={{ fontWeight: 600, color: "#374151" }}>
                      {wo.fileProgress || 0}%
                    </span>
                  </td>
                  <td className="mono small">
                    {wo.traceId?.substring(0, 15)}...
                  </td>
                  <td className="center">{wo.mode || "WEB"}</td>
                  <td>
                    <span className={`status-badge status-${wo.status}`}>
                      {wo.status === "completed"
                        ? "Processed"
                        : wo.status.charAt(0).toUpperCase() +
                          wo.status.slice(1)}
                    </span>
                  </td>
                  <td
                    style={{
                      fontWeight: 600,
                      color: formatWorkOrderRuntime(wo.duration).color,
                      background: formatWorkOrderRuntime(wo.duration).bg,
                      borderRadius: "3px",
                      padding: "2px 6px",
                    }}
                  >
                    {formatWorkOrderRuntime(wo.duration).display}
                  </td>
                  <td>
                    {wo.extractedFormType ||
                      wo.reportType?.substring(0, 30) ||
                      "-"}
                  </td>
                  <td className="date">{formatDate(wo.createdAt)}</td>
                  <td className="small">{wo.createdBy?.substring(0, 20)}</td>
                  <td>
                    <div className="reports-cell">
                      <button
                        className="btn-icon-small"
                        title="JSON Report"
                        onClick={() => handleDownloadReport(wo._id, "JSON")}
                      >
                        <FileJson size={16} />
                      </button>
                      <button
                        className="btn-icon-small"
                        title="Excel Report"
                        onClick={() => handleDownloadReport(wo._id, "Excel")}
                      >
                        <FileSpreadsheet size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "24px",
          padding: "16px",
          background: "#f8fafc",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div style={{ fontSize: "14px", color: "#64748b" }}>
          Showing {workOrders.length > 0 ? pagination.skip + 1 : 0} -{" "}
          {Math.min(pagination.skip + pagination.limit, pagination.total)} of{" "}
          {pagination.total} work orders
        </div>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            onClick={handlePrevPage}
            disabled={pagination.page <= 1}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "8px 12px",
              background: pagination.page <= 1 ? "#e2e8f0" : "#3b82f6",
              color: pagination.page <= 1 ? "#94a3b8" : "white",
              border: "none",
              borderRadius: "6px",
              cursor: pagination.page <= 1 ? "not-allowed" : "pointer",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            <ChevronLeft size={16} /> Previous
          </button>

          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
              (pageNum) => (
                <button
                  key={pageNum}
                  onClick={() =>
                    fetchWorkOrders((pageNum - 1) * pagination.limit)
                  }
                  style={{
                    width: "32px",
                    height: "32px",
                    padding: "0",
                    background:
                      pageNum === pagination.page ? "#3b82f6" : "#f1f5f9",
                    color: pageNum === pagination.page ? "white" : "#1e293b",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: pageNum === pagination.page ? "600" : "500",
                    transition: "all 0.2s",
                  }}
                >
                  {pageNum}
                </button>
              ),
            )}
          </div>

          <button
            onClick={handleNextPage}
            disabled={pagination.page >= pagination.pages}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "8px 12px",
              background:
                pagination.page >= pagination.pages ? "#e2e8f0" : "#3b82f6",
              color: pagination.page >= pagination.pages ? "#94a3b8" : "white",
              border: "none",
              borderRadius: "6px",
              cursor:
                pagination.page >= pagination.pages ? "not-allowed" : "pointer",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>

        <div style={{ fontSize: "14px", color: "#64748b" }}>
          Page {pagination.page} of {pagination.pages}
        </div>
      </div>
    </div>
  );
};

export default WorkOrdersList;
